import { refreshAsync, fetchDiscoveryAsync } from 'expo-auth-session';
import { env } from '@/lib/env';
import { logger } from '@/lib/logger';
import { AuthTokens } from '@/lib/types/auth';
import { persistentStorage } from '@/lib/storage';
import { authVar } from '@/lib/apollo/auth';
import { TokenRefresher } from '@/lib/auth/token-refresher';

async function performTokenRefresh(
  currentRefreshToken: string,
): Promise<AuthTokens> {
  logger.debug('Fetching discovery document for token refresh...');

  const discovery = await fetchDiscoveryAsync(
    env.zitadelAuthorityUrl.toString().replace(/\/$/, ''),
  );

  logger.debug('Refreshing access token...');

  const refreshedTokens = await refreshAsync(
    {
      clientId: env.zitadelClientId,
      refreshToken: currentRefreshToken,
    },
    discovery,
  );

  const updatedTokens: AuthTokens = {
    accessToken: refreshedTokens.accessToken,
    refreshToken: refreshedTokens.refreshToken ?? currentRefreshToken,
    expiresIn: refreshedTokens.expiresIn ?? 3600,
    issuedAt: Math.floor(Date.now() / 1000),
  };

  // Update storage and reactive var
  await persistentStorage.set('auth', updatedTokens);
  authVar(updatedTokens);

  logger.debug('Token refreshed and persisted successfully');

  return updatedTokens;
}

// Single-flight so the three startup refreshers (auth restore effect, Apollo error
// link, PowerSync connector) collapse to one network refresh — Zitadel rotates the
// refresh token on use, so overlapping calls would self-inflict a logout.
const tokenRefresher = new TokenRefresher(performTokenRefresh);

export function refreshToken(currentRefreshToken: string): Promise<AuthTokens> {
  return tokenRefresher.refresh(currentRefreshToken);
}
